import { CommonModule, isPlatformBrowser, NgOptimizedImage, PlatformLocation } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  TemplateRef,
  TransferState,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { isReadable, TinyColor } from '@ctrl/tinycolor';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTicket, faPlay, faTv } from '@fortawesome/free-solid-svg-icons';

import { ENVIRONMENT, MovieSiteHeaderImagesConfig, MovieSiteHeaderImg } from '@environment';
import { MovieDetailFeatureShowtimesModule } from '@usheru-hff/movie-detail/feature-showtimes';
import { MovieDetailFeatureStreamingModule } from '@usheru-hff/movie-detail/feature-streaming';
import { MovieDetailUiMovieElementsModule } from '@usheru-hff/movie-detail/ui-movie-elements';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MOVIE_TRANSFER_KEY } from '@usheru-hff/sdk/movie';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { SharedFeatureSidemenuComponent } from '@usheru-hff/shared/feature-sidemenu';
import { SharedFeatureSocialLinksModule } from '@usheru-hff/shared/feature-social-links';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { getDateFromDDMMYYYY } from '@usheru-hff/shared/utils';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { SharedUtilsTranslationModule, TranslationsService } from '@usheru-hff/shared/utils-translation';

import { BrandMovieService } from '../brand-movie.service';
import { SectionHyperLink } from '../feature-movie-awareness-page/feature-movie-awareness-page.component';

@Component({
  selector: 'feature-movie-conversion-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgOptimizedImage,
    FontAwesomeModule,
    MovieDetailUiMovieElementsModule,
    SharedFeatureSocialLinksModule,
    MovieDetailFeatureShowtimesModule,
    MovieDetailFeatureStreamingModule,
    SharedFeatureSidemenuComponent,
    SharedUtilsTranslationModule
  ],

  templateUrl: './feature-movie-conversion-page.component.html',
  styleUrl: './feature-movie-conversion-page.component.scss',
  providers: [BrandMovieService]
})
export class FeatureMovieConversionPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() movie: Movie | undefined;

  protected readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly PLATFORM_ID = inject(PLATFORM_ID);
  private readonly isPlatformBrowser = isPlatformBrowser(this.PLATFORM_ID);
  private readonly platformLocation = inject(PlatformLocation);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly channelService = inject(ChannelService);
  private readonly brandMovieService = inject(BrandMovieService);
  private readonly showtimeStreamingService = inject(ShowtimeStreamingService);
  private readonly videoService = inject(VideoFullscreenService);
  private readonly metaTagsService = inject(MetatagsService);
  private readonly imgService = inject(ImageService);
  private readonly translationsService = inject(TranslationsService);
  private readonly env = inject(ENVIRONMENT);
  private readonly transferState = inject(TransferState);
  private readonly titleService = inject(TitleService);
  private readonly trackingService = inject(TrackingService);
  readonly logoExternalLink: string | undefined = this.env?.movieAwarenessLogoExternalLink;

  @HostBinding('style') bindingStyle: {
    [rule: string]: string;
  } = {};
  @ViewChild(SharedFeatureSidemenuComponent) sidemenuComponent!: SharedFeatureSidemenuComponent;
  @ViewChild('footerWrapper') footerWrapperRef!: ElementRef<HTMLElement>;

  readonly desktopBreakpoint = 1200;
  private showtimesLoadedTracked = false;
  private streamingPlatformsLoadedTracked = false;

  tab = signal<'showtimes' | 'streaming'>('showtimes');
  get hasShowtimes() {
    return this.movie && this.showtimeStreamingService.determineInCinemasAvailability(this.movie!);
  }

  get hasStreaming() {
    return this.movie && this.showtimeStreamingService.determineStreamingAvailability(this.movie!);
  }

  releaseDate: Signal<Date | undefined>;
  channelName = signal<string | undefined>(undefined);
  channelLogoUrl = '';
  routePath = '.';
  isBrandColorReadable = signal<boolean>(true);
  sectionsHyperLink = signal<SectionHyperLink[]>([
    {
      label: 'movie.menu.film-info',
      placeholder: 'Film Info',
      ref: undefined
    }
  ]);

  hideLogoTitle = false;
  hideTitleText = false;

  inCinemaAddon!: TemplateRef<unknown>;
  appHasOnlyMovies = false;

  customHeaderImages = signal<MovieSiteHeaderImg[] | undefined>(undefined);

  readonly TrackingCategory = 'MovieDetail';
  faPlay = faPlay;
  faTicket = faTicket;
  faTv = faTv;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setShowtimesheaderHeight();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    this.checkStykyStyle();

    // this.setFixForHeaderBar();
  }

  get element() {
    return this.elementRef.nativeElement;
  }

  readonly ASSETSLOCKER_LABEL = 'movie-site.assets-locker-label';
  readonly ASSETSLOCKER_LABEL_BUTTON = 'movie-site.assets-locker';

  constructor() {
    effect(() => {
      if (this.movie) this.brandMovieService.setMetatags(this.movie!);
    });
  }

  ngOnInit() {
    this.appHasOnlyMovies = this.env?.appHasOnlyMovies || false;
    // this.brandMovieService.checkMovieInRouteState();

    // check if route is 'book-now' to show showtimes tab and 'watch-now' to show streaming tab
    const urlPart = this.route.snapshot.url[this.route.snapshot.url.length - 1].path || 'book-now';

    this.changeTab(
      urlPart === 'book-now' && this.hasShowtimes
        ? 'showtimes'
        : urlPart === 'watch-now' && this.hasStreaming
          ? 'streaming'
          : 'showtimes'
    );

    this.releaseDate = computed(() => {
      const release = this.movie?.releaseDate;
      return release ? getDateFromDDMMYYYY(release) || undefined : undefined;
    });

    //

    this.injectStyles();
    this.bindingStyle = this.setColorStyles();
    this.setEnvConfiguration();
    this.isBrandColorReadable.set(this.isRedeableBrandOverBackgroundColor());
    //
    this.setChannelLogo();
    // check if the route ends with 'book-now'
    const url = this.route.snapshot.url;

    this.routePath =
      url[url.length - 1].path === 'book-now' ? '..' : url[url.length - 1].path === 'watch-now' ? '..' : '.';
  }

  ngAfterViewInit() {
    this.checkTab();
    this.appendFooter();
    this.setInCinmeaAddon();
    if (this.movie?.faviconImageUrl) {
      this.titleService.setFavicon(this.movie?.faviconImageUrl);
    }
  }

  setEnvConfiguration() {
    const configs = this.env?.movieSiteConfig;
    if (!configs) return;
    const movie = this.movie?.friendlyUrl || '';
    const config = configs[movie];
    if (!config) return;
    this.setHeaderBackgroundColor(config.headerBackgroundColor);
    this.setCustomHeaderImages(config.headerImagesConfig);
    this.setConfigHideTitleLogo(config.conversionHideTitleLogo);
    this.setConfigHideTitleText(config.conversionHideTitleText);
  }

  setHeaderBackgroundColor(value?: string) {
    if (!value) return;
    this.bindingStyle['--header-background-color'] = value;
  }

  setCustomHeaderImages(config: MovieSiteHeaderImagesConfig | undefined) {
    if (!config) return;
    this.customHeaderImages.set(
      config.sources?.filter(img => img.filter === undefined || img.filter === 'conversion') || []
    );
  }

  setConfigHideTitleLogo(value?: boolean) {
    if (!value) return;
    this.hideLogoTitle = value;
  }

  setConfigHideTitleText(value?: boolean) {
    if (!value) return;
    this.hideTitleText = value;
  }

  setChannelLogo() {
    this.channelName.set(this.channelService.channel.name);
    this.channelLogoUrl = this.channelService.channel.logoUrl;
  }

  setServiceSettings() {
    this.brandMovieService.sidemenuRef = this.sidemenuComponent;
  }

  setColorStyles() {
    const brandColor = this.movie?.settings?.movieSiteBrandColor || '#000000';
    const backgroundColor = this.movie?.settings?.movieSiteBackgroundColor || '#ffffff';
    const borderRadious = this.movie?.settings?.selectShowtimeContainerBorderRadius || '12px';
    return this.brandMovieService.setColorStyles(brandColor, backgroundColor, borderRadious);
  }

  injectStyles() {
    if (!this.isPlatformBrowser) return;
    const fontFamily = this.movie?.settings?.movieSiteNormalFontFile || 'Arial';
    const fontFamilyBold = this.movie?.settings?.movieSiteBoldFontFile || 'Arial';
    this.brandMovieService.injectStyles(fontFamily, fontFamilyBold);
  }

  private setShowtimesheaderHeight() {
    const host = this.elementRef.nativeElement as HTMLElement;
    const header = document.querySelector('.ush-showtimes-header') as HTMLElement;
    if (!header) return;
    const height = header.clientHeight;
    host.style?.setProperty('--showtimes-header-height', `${height}px`);
  }

  checkStykyStyle() {
    const host = this.elementRef.nativeElement as HTMLElement;
    const header = document.querySelector('.ush-showtimes-header') as HTMLElement;
    const wrapper = document.querySelector('.ush-showtimes-wrapper') as HTMLElement;
    if (!header || !wrapper) return;
    if (header.offsetTop !== wrapper.offsetTop) {
      host.classList.add('stycked');
    } else {
      host.classList.remove('stycked');
    }
  }

  get isDesktop() {
    return window.innerWidth >= this.desktopBreakpoint;
  }

  playTrailer() {
    this.videoService.play({
      content: this.movie as Movie,
      category: this.TrackingCategory,
      animate: true,
      videoSettings: { muted: false, autoplay: true }
    });
  }

  setMetaTags(movie?: Movie): void {
    if (!movie) {
      this.metaTagsService.resetMetaTags();
      return;
    }

    //

    const metaTags: MetaTags = {
      url: this.platformLocation.pathname,
      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : `meta.page.movie-detail.title`,

      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : `meta.page.movie-detail.description`,
      video: movie.trTrailerLink
    };

    metaTags.favicon = movie?.faviconImageUrl;
    if (movie.tilePhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.tilePhotoUrl, { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.portraitPhotoUrl, { height: 1000 });
    }
    //TODO cases with more directors?
    const director = movie?.movieCrew?.find(mc => {
      if (!mc.crewRole?.name) return false;

      return mc.crewRole.name.toLowerCase() == 'director';
    })?.star?.name;
    this.metaTagsService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  appendFooter() {
    this.brandMovieService.moveFooter(this.footerWrapperRef.nativeElement);
    this.brandMovieService.injectCustomFooterStyle(`
        ush-footer {
          width: 100%;
        }
      `);
  }

  checkTab() {
    const path = this.route.snapshot.routeConfig?.path;
    if (path?.includes('book-now') && this.hasShowtimes) {
      this.tab.set('showtimes');
    } else if (path?.includes('watch-now') && this.hasStreaming) {
      this.tab.set('streaming');
    }
  }

  changeTab(tab: 'showtimes' | 'streaming') {
    const newPath = tab === 'showtimes' ? 'book-now' : 'watch-now';
    // check if router has the 'book-now' or 'watch-now' path and replace it with the new one

    if (this.router.url.endsWith('book-now') || this.router.url.endsWith('watch-now')) {
      this.router.navigateByUrl(this.router.url.replace(/(book-now|watch-now)/, newPath), {
        replaceUrl: true
      });
    } else {
      this.router.navigateByUrl(`${this.router.url}/${newPath}`, {
        replaceUrl: true
      });
    }
    this.tab.set(tab);
  }

  isRedeableBrandOverBackgroundColor() {
    const brandColor = new TinyColor(this.movie?.settings?.movieSiteBrandColor || '#000000');
    const backgroundColor = new TinyColor(this.movie?.settings?.movieSiteBackgroundColor || '#ffffff');
    return isReadable(brandColor, backgroundColor);
  }

  setInCinmeaAddon() {
    this.inCinemaAddon = this.templateService.get('brandedInCinemasAddon');
  }

  get templateService() {
    return this.channelService.templatesService;
  }

  get hasAssets() {
    return this.movie?.hasAssets || false;
  }

  /**
   * Method that will track ShowtimesLoaded action if ther are performances present.
   */
  onShowtimesLoaded() {
    // only track if performances exists for this movie
    if (!this.showtimesLoadedTracked && this.movie) {
      this.showtimesLoadedTracked = true;
      this.trackingService.trackEvent({
        action: 'ShowtimesLoaded',
        properties: {
          category: 'MovieShowtimes',
          label: `${this.movie?.title}`,
          movie: `${this.movie?.title}`,
          idMovie: this.movie.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie)
        }
      });
    }
  }

  onStreamingPlatformsLoaded() {
    if (!this.streamingPlatformsLoadedTracked && this.movie) {
      this.streamingPlatformsLoadedTracked = true;
      this.trackingService.trackEvent({
        action: 'StreamingLoaded',
        properties: {
          category: 'MovieStreaming',
          label: `${this.movie?.title}`,
          movie: `${this.movie?.title}`,
          idMovie: this.movie?.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie)
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.metaTagsService.resetMetaTags();
    this.brandMovieService.removeStyles();
    this.brandMovieService.restoreFooter();
    this.transferState.remove(MOVIE_TRANSFER_KEY);
  }
}
